import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MDBIcon } from "mdb-react-ui-kit";

import { NavMenu } from "Projects/CC_cubi_clan_project/GlobalComponents/NavMenu/NavMenu";
import { LoadingSpinner } from "Projects/CC_cubi_clan_project/GlobalComponents/LoadingSpinner/LoadingSpinner";
import { handleNavLinks } from "Projects/CC_cubi_clan_project/Utils/Utils";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { getMfmMyRole, getMfmSystemStatus } from "Projects/CC_cubi_clan_project/Utils/MfmService";

import MFMLoginScreen from "../../Components/MFMLoginScreen/MFMLoginScreen";
import MFMHomeView from "../MFMHomeView/MFMHomeView";
import MFMProcessesView from "../MFMProcessesView/MFMProcessesView";
import MFMStatusView from "../MFMStatusView/MFMStatusView";
import MFMTrashView from "../MFMTrashView/MFMTrashView";
import LibraryTree from "../../Components/LibraryTree/LibraryTree";

import "./MFMMainView.css";

const SIDEBAR_WIDTH_MIN = 200;
const SIDEBAR_WIDTH_MAX = 520;
const SIDEBAR_WIDTH_DEFAULT = 220;
const SIDEBAR_WIDTH_STORAGE_KEY = "mfm.sidebar.width";

function clampSidebarWidth(width) {
  return Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, width));
}

function getInitialSidebarWidth() {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return SIDEBAR_WIDTH_DEFAULT;
    return clampSidebarWidth(parsed);
  } catch (_) {
    return SIDEBAR_WIDTH_DEFAULT;
  }
}

// Estados posibles: "checking" | "no-session" | "no-access" | "granted"
export default function MFMMainView() {
  const [authStatus, setAuthStatus] = useState("checking");
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [navActive, setNavActive] = useState("Library");
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(getInitialSidebarWidth);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    hasWorkInProgress: false,
    pending: 0,
    inProgress: 0,
    processingFiles: 0,
    loading: false,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const isResizingSidebarRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(SIDEBAR_WIDTH_DEFAULT);
  const rawViewMode = new URLSearchParams(location.search).get("view");
  const viewMode = rawViewMode === "trash" || rawViewMode === "files" || rawViewMode === "home" || rawViewMode === "processes"
    ? rawViewMode
    : "home";

  const refreshSystemStatus = useCallback(async () => {
    setSystemStatus((prev) => ({ ...prev, loading: true }));
    try {
      const data = await getMfmSystemStatus();
      const queue = data?.queue || {};
      const summary = data?.summary || {};
      const pending = Number(queue?.pending || 0);
      const inProgress = Number(queue?.inProgress || 0);
      const processingFiles = Number(summary?.processingFiles || 0);
      setSystemStatus({
        hasWorkInProgress: !!queue?.hasWorkInProgress,
        pending,
        inProgress,
        processingFiles,
        loading: false,
      });
    } catch (_) {
      setSystemStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const checkMfmAccess = useCallback(async () => {
    setAuthStatus("checking");
    try {
      const res = await doFetch("MFM", "libraries", { _silent: true });
      if (!res) { setAuthStatus("no-access"); return; }
      if (res.status === 200) {
        setAuthStatus("granted");
        try {
          const role = await getMfmMyRole();
          setIsAdmin(role?.isAdmin === true);
          setIsManager(role?.isManager === true);
        } catch (e) { console.warn("[MFM] my-role error:", e); setIsAdmin(false); setIsManager(false); }
      }
      else if (res.status === 401) { setAuthStatus("no-session"); }
      else { setAuthStatus("no-access"); }
    } catch (e) {
      console.error("[MFM] checkMfmAccess exception:", e);
      setAuthStatus("no-access");
    }
  }, []);

  // Comprobación inicial al montar — la API determina el estado de sesión
  useEffect(() => {
    checkMfmAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authStatus !== "granted") return;
    refreshSystemStatus();
    const timer = window.setInterval(() => refreshSystemStatus(), 15000);
    return () => window.clearInterval(timer);
  }, [authStatus, refreshSystemStatus]);

  const navMenu = [
    {
      devName: "Home",
      label: "Inicio",
      active: navActive === "Home",
      visible: true,
      disabled: false,
      url: "/mfm?view=home",
    },
    {
      devName: "Library",
      label: "Ficheros",
      active: navActive === "Library",
      visible: true,
      disabled: false,
      url: "/mfm?view=files",
    },
    {
      devName: "Trash",
      label: "Papelera",
      active: navActive === "Trash",
      visible: true,
      disabled: false,
      url: "/mfm?view=trash",
    },
    {
      devName: "Processes",
      label: "Procesos",
      active: navActive === "Processes",
      visible: true,
      disabled: false,
      url: "/mfm?view=processes",
    },
    {
      devName: "AppList",
      label: "Aplicaciones",
      active: navActive === "AppList",
      visible: true,
      disabled: false,
      url: "/home",
    },
  ];

  useEffect(() => {
    if (!location.pathname.startsWith("/mfm")) return;
    if (viewMode === "trash") {
      setNavActive("Trash");
    } else if (viewMode === "processes") {
      setNavActive("Processes");
    } else if (viewMode === "files") {
      setNavActive("Library");
    } else {
      setNavActive("Home");
    }
  }, [location.pathname, viewMode]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 991.98px)");
    const apply = (matches) => {
      setIsMobileViewport(matches);
      if (!matches) {
        setIsMobileSidebarOpen(false);
      }
    };

    apply(media.matches);
    const onChange = (event) => apply(event.matches);

    if (media.addEventListener) {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
    } catch (_) {
      // Ignorar errores de storage (modo privado, cuota, etc.).
    }
  }, [sidebarWidth]);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!isResizingSidebarRef.current) return;
      const delta = event.clientX - resizeStartXRef.current;
      setSidebarWidth(clampSidebarWidth(resizeStartWidthRef.current + delta));
    };

    const onMouseUp = () => {
      if (!isResizingSidebarRef.current) return;
      isResizingSidebarRef.current = false;
      document.body.classList.remove("mfm-resizing-sidebar");
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.classList.remove("mfm-resizing-sidebar");
    };
  }, []);

  const onSidebarResizeMouseDown = (event) => {
    if (isMobileViewport) return;
    event.preventDefault();
    isResizingSidebarRef.current = true;
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = sidebarWidth;
    document.body.classList.add("mfm-resizing-sidebar");
  };

  const onLibrarySelect = (librarySelection) => {
    setSelectedLibrary(librarySelection);
    if (isMobileViewport) {
      setIsMobileSidebarOpen(false);
    }
  };

  const onHandleNavLinks = (event, url) => {
    if (event?.target?.name === "UserDetail") {
      navigate("/profile");
      return;
    }
    handleNavLinks(event, url, navigate, setNavActive);
  };

  const onUserLoggedIn = (loggedIn) => {
    if (loggedIn) {
      checkMfmAccess();
    } else {
      setAuthStatus("no-session");
      toast.show({
        message: "Sesión cerrada",
        type: "info",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 3000,
      });
    }
  };

  // --- Pantalla de carga mientras se verifican permisos ---
  if (authStatus === "checking") {
    return <LoadingSpinner isLoading={true} />;
  }

  // --- Sin sesión: solo login, nada más accesible ---
  if (authStatus === "no-session") {
    return <MFMLoginScreen onLoggedIn={onUserLoggedIn} />;
  }

  // --- Con sesión pero sin permiso APP_ACCESS ---
  if (authStatus === "no-access") {
    return (
      <div className="mfm-no-access">
        <MDBIcon fas icon="ban" className="mfm-no-access-icon" />
        <h4>Sin acceso</h4>
        <p>Tu usuario no tiene permiso para acceder a Media File Manager.</p>
        <p className="mfm-no-access-hint">
          Si crees que es un error, contacta con el administrador.
        </p>
      </div>
    );
  }

  // --- Con sesión y con acceso: layout completo ---
  return (
    <div className="mfm-app">
      <LoadingSpinner isLoading={isLoading} />
      <NavMenu
        title="MFM"
        appCode="MFM"
        navMenu={navMenu}
        onHandleNavLinks={onHandleNavLinks}
        onLoading={setIsLoading}
        onNavigate={navigate}
        onLoggedIn={onUserLoggedIn}
        statusChip={{
          icon: systemStatus.hasWorkInProgress ? "cogs" : "check-circle",
          text: systemStatus.loading
            ? "Estado..."
            : (systemStatus.hasWorkInProgress
                ? `Procesando ${systemStatus.inProgress + systemStatus.pending} tarea(s)`
                : "Sin proceso activo"),
          tone: systemStatus.hasWorkInProgress ? "working" : "ok",
          pulse: systemStatus.hasWorkInProgress,
        }}
      />
      <div className="mfm-layout">
        {viewMode === "files" && (
          <>
            <aside
              className={`mfm-sidebar${isMobileViewport ? " mfm-sidebar--mobile" : ""}${isMobileSidebarOpen ? " mfm-sidebar--mobile-open" : ""}`}
              style={isMobileViewport ? undefined : { width: sidebarWidth, minWidth: sidebarWidth }}
            >
              <LibraryTree
                selected={selectedLibrary}
                onSelect={onLibrarySelect}
                refreshKey={libraryRefreshKey}
                onLibraryChanged={() => setLibraryRefreshKey((k) => k + 1)}
              />
            </aside>
            {!isMobileViewport && (
              <div
                className="mfm-sidebar-resizer"
                role="separator"
                aria-orientation="vertical"
                aria-label="Redimensionar panel de bibliotecas"
                onMouseDown={onSidebarResizeMouseDown}
              />
            )}
            {isMobileViewport && isMobileSidebarOpen && (
              <button
                type="button"
                className="mfm-sidebar-backdrop"
                aria-label="Cerrar panel de bibliotecas"
                onClick={() => setIsMobileSidebarOpen(false)}
              />
            )}
          </>
        )}
        <main className="mfm-main">
          {isMobileViewport && viewMode === "files" && (
            <button
              type="button"
              className="mfm-mobile-sidebar-toggle"
              onClick={() => setIsMobileSidebarOpen((v) => !v)}
            >
              <MDBIcon fas icon="folder-tree" className="me-2" />
              Bibliotecas
            </button>
          )}
          {viewMode === "home" && (
            <MFMStatusView appCode={selectedLibrary?.appCode} selectedLibrary={selectedLibrary} />
          )}
          {viewMode === "trash" && (
            <MFMTrashView />
          )}
          {viewMode === "processes" && (
            <MFMProcessesView selectedLibrary={selectedLibrary} />
          )}
          {viewMode === "files" && (
            <MFMHomeView
              selectedLibrary={selectedLibrary}
              onLibraryChanged={() => setLibraryRefreshKey((k) => k + 1)}
              isAdmin={isAdmin}
              isManager={isManager}
              viewMode="files"
            />
          )}
        </main>
      </div>
    </div>
  );
}
